import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandXS,
  BrandXXS,
  HeadlineM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { CardSetLabel } from '@sorare/core/src/components/collections/CardSetLabel';
import { CardsNumber } from '@sorare/core/src/components/collections/CardsNumber';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';
import { glossary } from '@sorare/core/src/lib/glossary';

import { Neon } from '#src/components/collections/CollectionPreview/Neon/index.tsx';
import inSeasonBackground from '#src/components/collections/CollectionPreview/assets/in-season.webp';
import { useCardCollectionData } from '#src/components/collections/CollectionPreview/useCardCollectionData.ts';

import type { CollectionPreviewHeader_cardCollection } from './__generated__/index.graphql.ts';

const Header = styled(Horizontal).attrs({ gap: 2, center: true })`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: var(--triple-unit) var(--double-unit);
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  border-bottom: none;
  background:
    linear-gradient(
      0deg,
      #000 0%,
      rgba(0, 0, 0, 0.58) 71.75%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--collection-color-rgb), 0.4) 0%,
      rgba(var(--collection-color-rgb), 0.4) 100%
    ),
    var(--background-image) center / cover no-repeat;
`;

const FullWidthSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
`;

const Logo = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
`;

const Completed = styled(BrandXXS)`
  position: absolute;
  top: var(--unit);
  left: var(--double-unit);
  color: var(--c-onBase-success);
`;

const HeaderRow = styled(Horizontal).attrs({ gap: 1 })`
  align-items: baseline;
`;

type Props = {
  collection: CollectionPreviewHeader_cardCollection;
  shouldBeHighlighted?: boolean;
  to?: string;
};

export const CollectionPreviewHeader = ({
  collection,
  shouldBeHighlighted,
  to,
}: Props) => {
  const { rarity, team, userCardCollection, inSeason, cardSet } = collection;
  const { pictureUrl, name, shortName } = team || {};
  const { displayedStartYear, displayedEndYear, backgroundColor, rgbColor } =
    useCardCollectionData(collection);
  const seasonLabel = displayedStartYear
    ? `${displayedStartYear}${displayedEndYear ? `-${displayedEndYear}` : ''}`
    : '';
  const { complete, slotsCount, fulfilledSlotsCount } =
    userCardCollection || {};
  const showCompleted = !!complete;

  if (!team) {
    return null;
  }

  return (
    <Header
      style={
        {
          '--background-image': inSeason
            ? `url(${inSeasonBackground})`
            : backgroundColor,
          '--collection-color-rgb': rgbColor || 'var(--c-rgb-black)',
          '--collection-color': backgroundColor,
        } as CSSProperties
      }
      as={to ? Link : 'div'}
      to={to}
    >
      {showCompleted ? (
        <Completed color="var(--c-onBase-success)">
          <FormattedMessage {...glossary.completed} />
        </Completed>
      ) : null}
      <FullWidthSBHorizontal>
        <FullWidthHorizontal>
          <OnboardingHighlightWrapper
            highlightName="CollectionPreviewPicture"
            wrap={!!shouldBeHighlighted}
          >
            {!!pictureUrl && <Logo alt="" src={pictureUrl} />}
          </OnboardingHighlightWrapper>
          <Vertical gap={0}>
            <OnboardingHighlightWrapper
              highlightName="CollectionPreviewName"
              wrap={!!shouldBeHighlighted}
            >
              <HeadlineM>{name || shortName}</HeadlineM>
            </OnboardingHighlightWrapper>
            <HeaderRow>
              {cardSet && isSpecialCardSet(cardSet.slug) ? (
                <LabelS bold>
                  <CardSetLabel cardSet={cardSet} />
                </LabelS>
              ) : (
                <>
                  <LabelS>
                    <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
                  </LabelS>
                  <BrandXS
                    bold
                    className={classNames({
                      'text-gradient-color': inSeason,
                    })}
                    style={
                      {
                        '--text-gradient-color': `linear-gradient(
                      45deg,
                      #f8d3da,
                      #b3a9f4 28%,
                      #fbe9fb 54%,
                      #4f94fd 100%
                      )`,
                      } as CSSProperties
                    }
                  >
                    {seasonLabel}
                  </BrandXS>
                </>
              )}
            </HeaderRow>
          </Vertical>
        </FullWidthHorizontal>
        <OnboardingHighlightWrapper
          wrap={!!shouldBeHighlighted}
          highlightName="CollectionPreviewStatsCards"
        >
          <CardsNumber
            ownedCards={fulfilledSlotsCount}
            totalCards={slotsCount}
          />
        </OnboardingHighlightWrapper>
      </FullWidthSBHorizontal>
      <Neon color={backgroundColor} />
    </Header>
  );
};

CollectionPreviewHeader.fragments = {
  cardCollection: gql`
    fragment CollectionPreviewHeader_cardCollection on CardCollection {
      slug
      rarity
      inSeason
      cardSet {
        slug
        ...CardSetLabel_cardSet
      }
      team: anyTeam {
        slug
        name
        shortName
        pictureUrl
        styleGuideColor
      }
      userCardCollection(forUserSlug: $user) {
        id
        slotsCount
        fulfilledSlotsCount
        complete
      }
      ...useCardCollectionData_cardCollection
    }
    ${useCardCollectionData.fragments.cardCollection}
    ${CardSetLabel.fragments.cardSet}
  ` as TypedDocumentNode<CollectionPreviewHeader_cardCollection>,
};
